import { Component, type ErrorInfo, type ReactNode } from "react";
import RouteError from "@/pages/route-error";

interface Props {
  children: ReactNode;
  /** Changing this value clears a captured error — used to recover on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Replaces the Next.js `error.tsx` convention. A render error anywhere below is
 * caught here and shown as a recoverable page rather than a blank screen, and
 * navigating elsewhere clears it automatically.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return <RouteError error={this.state.error} reset={this.reset} />;
    }

    return this.props.children;
  }
}
